import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import { isDesktop } from '@/lib/platform';
import { pickDroppedPdf, prepareDroppedPdf } from '@/lib/fileDrop';

/**
 * useDesktopFileDrop — "Drag a file anywhere in this window" for the Electron
 * shell. Listens at the document level so every page under DesktopShell
 * accepts a PDF drop; opens it in the viewer.
 *
 * Pages that already own a dropzone (Merge, Split, Convert, …) call
 * preventDefault + stopPropagation in their own handlers, and this hook
 * additionally ignores drops that a page marked handled
 * (`event.defaultPrevented`) so their zones keep working unchanged. The
 * viewer is skipped too: replacing an open document with unsaved edits by
 * accident would be worse than doing nothing.
 */
export function useDesktopFileDrop() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isDesktop()) return undefined;

    const onDragOver = (e) => {
      // Without this Chromium treats the drop as a navigation to file://.
      if (e.dataTransfer?.types?.includes('Files')) {
        e.preventDefault();
        e.dataTransfer.dropEffect = location.pathname === '/viewer' ? 'none' : 'copy';
      }
    };

    const onDrop = async (e) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      if (e.defaultPrevented) return; // a page-level dropzone took it
      e.preventDefault();
      if (location.pathname === '/viewer') return;
      const dropped = pickDroppedPdf(e.dataTransfer);
      if (!dropped) return;
      try {
        const { file, filePath } = await prepareDroppedPdf(dropped);
        navigate('/viewer', { state: { newDocument: true, file, filePath } });
      } catch (err) {
        console.error('Failed to open dropped file:', err);
      }
    };

    document.addEventListener('dragover', onDragOver);
    document.addEventListener('drop', onDrop);
    return () => {
      document.removeEventListener('dragover', onDragOver);
      document.removeEventListener('drop', onDrop);
    };
  }, [navigate, location.pathname]);
}
